import { createGlobalStyle } from 'styled-components';
import { theme } from '../tokens';

export const GlobalStyles = createGlobalStyle`
  /* CSS Reset */
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* Root element */
  html {
    font-size: 16px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    scroll-behavior: smooth;

    @media (prefers-reduced-motion: reduce) {
      scroll-behavior: auto;
    }
  }

  /* Body */
  body {
    font-family: ${theme.fontFamilies.sans};
    font-size: ${theme.fontSizes.base};
    font-weight: ${theme.fontWeights.normal};
    line-height: ${theme.lineHeights.normal};
    color: ${theme.colors.text.primary};
    background-color: ${theme.colors.background.primary};
    min-height: 100vh;
  }

  /* Root container */
  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  /* Typography */
  h1, h2, h3, h4, h5, h6 {
    font-family: ${theme.fontFamilies.sans};
    font-weight: ${theme.fontWeights.semibold};
    line-height: ${theme.lineHeights.tight};
    color: ${theme.colors.text.primary};
    margin: 0;
  }

  h1 {
    font-size: ${theme.fontSizes['4xl']};
  }

  h2 {
    font-size: ${theme.fontSizes['3xl']};
  }

  h3 {
    font-size: ${theme.fontSizes['2xl']};
  }

  h4 {
    font-size: ${theme.fontSizes.xl};
  }

  h5 {
    font-size: ${theme.fontSizes.lg};
  }

  h6 {
    font-size: ${theme.fontSizes.base};
  }

  p {
    margin: 0;
    line-height: ${theme.lineHeights.relaxed};
  }

  /* Links */
  a {
    color: ${theme.colors.text.link};
    text-decoration: none;
    transition: color ${theme.durations.fast} ${theme.easings.ease};

    &:hover {
      color: ${theme.colors.text.linkHover};
    }

    &:focus-visible {
      outline: 2px solid ${theme.colors.primary[500]};
      outline-offset: 2px;
      border-radius: ${theme.radii.sm};
    }
  }

  /* Images */
  img, picture, video, canvas, svg {
    display: block;
    max-width: 100%;
  }

  img {
    height: auto;
  }

  /* Form elements */
  input, button, textarea, select {
    font: inherit;
    color: inherit;
  }

  button {
    cursor: pointer;
    background: none;
    border: none;
  }

  /* Lists */
  ul, ol {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  /* Tables */
  table {
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
  }

  /* Scrollbar styling */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${theme.colors.neutral[100]};
    border-radius: ${theme.radii.full};
  }

  ::-webkit-scrollbar-thumb {
    background: ${theme.colors.neutral[300]};
    border-radius: ${theme.radii.full};

    &:hover {
      background: ${theme.colors.neutral[400]};
    }
  }

  /* Firefox scrollbar */
  * {
    scrollbar-width: thin;
    scrollbar-color: ${theme.colors.neutral[300]} ${theme.colors.neutral[100]};
  }

  /* Selection */
  ::selection {
    background: ${theme.colors.primary[100]};
    color: ${theme.colors.primary[900]};
  }

  /* Focus visible for accessibility */
  :focus-visible {
    outline: 2px solid ${theme.colors.primary[500]};
    outline-offset: 2px;
  }

  :focus:not(:focus-visible) {
    outline: none;
  }

  /* Reduced motion */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* Screen reader only utility */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Container utility */
  .container {
    width: 100%;
    max-width: ${theme.containerWidths['2xl']};
    margin: 0 auto;
    padding: 0 ${theme.spacing[4]};

    ${theme.media.md} {
      padding: 0 ${theme.spacing[6]};
    }

    ${theme.media.lg} {
      padding: 0 ${theme.spacing[8]};
    }
  }

  /* Prose styling for content */
  .prose {
    h1, h2, h3, h4, h5, h6 {
      margin-top: 1.5em;
      margin-bottom: 0.5em;

      &:first-child {
        margin-top: 0;
      }
    }

    p {
      margin-bottom: 1em;
    }

    ul, ol {
      margin-bottom: 1em;
      padding-left: 1.5em;
    }

    ul {
      list-style-type: disc;
    }

    ol {
      list-style-type: decimal;
    }

    li {
      margin-bottom: 0.5em;
    }

    a {
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    blockquote {
      border-left: 4px solid ${theme.colors.primary[500]};
      padding-left: ${theme.spacing[4]};
      margin: ${theme.spacing[4]} 0;
      font-style: italic;
      color: ${theme.colors.text.secondary};
    }

    code {
      font-family: ${theme.fontFamilies.mono};
      font-size: 0.875em;
      background: ${theme.colors.neutral[100]};
      padding: 0.2em 0.4em;
      border-radius: ${theme.radii.sm};
    }

    pre {
      font-family: ${theme.fontFamilies.mono};
      font-size: 0.875em;
      background: ${theme.colors.neutral[900]};
      color: ${theme.colors.neutral[100]};
      padding: ${theme.spacing[4]};
      border-radius: ${theme.radii.lg};
      overflow-x: auto;
      margin: ${theme.spacing[4]} 0;

      code {
        background: none;
        padding: 0;
      }
    }
  }
`;

export default GlobalStyles;
